import type { BrandDocument } from './brand';

/**
 * Compiled-in brand documents, keyed by brand key (docs/17 §6).
 *
 * ⚠️ These are LIVE, not leftovers. The `?brand=<name>` query preview that originally
 * activated them is retired (steward-app#664), but the constants have a second,
 * unrelated consumer that is still shipping: the public intake page resolves its firm
 * brand through `intakeBrand()` (`src/intake/IntakeBrandScope.tsx`), keyed by the firm
 * registry's `brandKey` — not by any query parameter. Deleting this file would unbrand
 * every firm's intake form.
 *
 * Presentation only, and injected without persistence by `IntakeBrandScope`: nothing
 * here reaches localStorage, the host-keyed flash cache, or the favicon.
 */
export const PREVIEW_BRANDS: Record<string, BrandDocument> = {
  /**
   * RIPL Wealth (riplwealth.com) — palette and type lifted from their site:
   * "void" #130F2A, deep violet #2A2250, gold #C9963A, silver #F4F3F8;
   * Rokkitt slab-serif headings, DM Sans body. "Build Tomorrow Today."
   */
  ripl: {
    brandVersion: 1,
    productName: 'RIPL Wealth Portal',
    // Kicker is their tagline, not the firm name — matches riplwealth.com and their
    // intake form, both of which pair the wordmark with "Build Tomorrow Today"
    // rather than "RIPL Wealth". Verified against their live intake page's actual
    // DOM text content (not just the rendered pixels): title case, no trailing
    // period — their marketing site's tagline IMAGE separately carries a period
    // ("Build tomorrow today."), but the intake lockup we're replicating does not.
    // BrandLockup's kicker already applies `uppercase` in CSS (text-transform,
    // matching their own stylesheet), so the stored casing here is cosmetic at
    // render time either way — kept title case anyway for source-string fidelity
    // to what they actually authored, not because rendering depends on it.
    wordmark: { text: 'RIPL', kicker: 'Build Tomorrow Today' },
    // Self-hosted under public/ (served from this app's own origin, so no img-src
    // widening — see the doc comment on logoDark in brand.ts). We only have the
    // white wordmark RIPL supplied, which reads on their dark navy — hence
    // logoDark only; logoLight is intentionally absent so light mode keeps the
    // text lockup (BrandLockup falls back when the needed variant is missing).
    logoDark: '/brands/ripl/ripl-wordmark.png',
    fonts: {
      serif: "Rokkitt, Georgia, serif",
      sans: "'DM Sans', system-ui, sans-serif",
      display: "Rokkitt, Georgia, serif",
      stylesheetUrl:
        'https://fonts.googleapis.com/css2?family=Rokkitt:wght@400;500;600&family=DM+Sans:wght@400;500;600;700&display=swap',
    },
    palette: {
      light: {
        navy: '#130F2A',
        cream: '#F4F3F8',
        surface: '#FFFFFF',
        bronze: '#C9963A',
        slate: '#5D6C7B',
        body: '#232032',
        mid: '#4A4656',
        danger: '#BA1A1A',
        tag: '#77602C',
        ink: '#130F2A',
      },
      dark: {
        navy: '#EAE7F2',
        cream: '#130F2A',
        surface: '#1B1733',
        bronze: '#D9B25F',
        slate: '#9A94B0',
        body: '#E7E4F0',
        mid: '#B0ACC0',
        danger: '#E0726B',
        tag: '#D9C08A',
        ink: '#130F2A',
      },
    },
    modes: ['light', 'dark', 'system'],
    locked: true,
  },
};
